import React, { useEffect, useState, useContext, useMemo } from "react";
import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FyContext } from "@/components/shell/AppShell";
import { StatCard, GradeBadge, StatusBadge, SectionCard, EmptyState } from "@/components/common/Primitives";
import { TID } from "@/constants/testIds";
import { ClipboardList, CheckCircle2, TimerReset, AlertOctagon, Gauge, Award, PlayCircle, Users, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DONUT_COLORS = ["#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];
const GRADE_COLORS = ["#10B981", "#1D4ED8", "#F59E0B", "#EF4444"];
const MY_ASSIGN_PAGE_SIZES = [5, 10, 20, 50];

export default function DashboardPage() {
  const { user } = useAuth();
  const { fy } = useContext(FyContext);
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myPage, setMyPage] = useState(1);
  const [myPageSize, setMyPageSize] = useState(5);

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard?fy=${fy}`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [fy]);

  useEffect(() => { setMyPage(1); }, [myPageSize, data]);

  const myAssignmentsPage = useMemo(() => {
    const rows = data?.my_assignments || [];
    const totalPages = Math.max(1, Math.ceil(rows.length / myPageSize));
    const current = Math.min(myPage, totalPages);
    const start = (current - 1) * myPageSize;
    return { rows, page: current, totalPages, pageStart: start, pageRows: rows.slice(start, start + myPageSize) };
  }, [data, myPage, myPageSize]);

  if (loading || !data) return <div className="text-slate-500">Loading dashboard...</div>;

  const stats = data.stats;
  const isManager = user.role === "system_admin" || user.role === "manager";
  const isSDAdmin = user.role === "sd_admin";
  const isAuditor = user.role === "auditor";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Overview</div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
            {isAuditor ? "Auditor Console" : isSDAdmin ? "SD Admin Dashboard" : "Executive Dashboard"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {isAuditor ? `Your assignments and today's audits for FY ${fy}.` :
             isSDAdmin ? `Monthly planning progress and audit assignments for FY ${fy}.` :
             `Enterprise KPIs and audit performance for FY ${fy}.`}
          </p>
        </div>
      </div>

      {/* 6 stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard testId={TID.dashboard.statCard("total_plan")} label="Total Audit Plan" value={stats.total_plan.value} yoy={stats.total_plan.yoy} icon={ClipboardList} tone="primary" />
        <StatCard testId={TID.dashboard.statCard("completed")} label="Completed" value={stats.completed.value} yoy={stats.completed.yoy} icon={CheckCircle2} tone="completed" />
        <StatCard testId={TID.dashboard.statCard("in_progress")} label="In Progress" value={stats.in_progress.value} yoy={stats.in_progress.yoy} icon={TimerReset} tone="in_progress" />
        <StatCard testId={TID.dashboard.statCard("overdue")} label="Overdue" value={stats.overdue.value} yoy={stats.overdue.yoy} icon={AlertOctagon} tone="overdue" />
        <StatCard testId={TID.dashboard.statCard("avg_score")} label="Avg Score" value={stats.avg_score.value} yoy={stats.avg_score.yoy} icon={Gauge} tone="primary" suffix="%" />
        <StatCard testId={TID.dashboard.statCard("avg_grade")} label="Avg Grade" value={stats.avg_grade.value} yoy={stats.avg_grade.yoy} icon={Award} tone="planned" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
        <SectionCard className="lg:col-span-2" title="Audit Completion" subtitle="Current fiscal year status">
          <div data-testid={TID.dashboard.donutCompletion}>
            <Chart
              type="donut" height={280}
              series={[data.completion_overview.completed, data.completion_overview.in_progress, data.completion_overview.overdue, data.completion_overview.planned]}
              options={{
                labels: ["Completed", "In Progress", "Overdue", "Planned"],
                colors: DONUT_COLORS,
                chart: { fontFamily: "IBM Plex Sans", toolbar: { show: false } },
                stroke: { width: 2, colors: ["#fff"] },
                legend: { position: "bottom", fontSize: "12px" },
                dataLabels: { enabled: true, style: { fontSize: "11px", fontWeight: 700 } },
                plotOptions: { pie: { donut: { size: "62%", labels: { show: true, total: { show: true, label: "Total", fontSize: "12px", color: "#64748B" } } } } },
              }}
            />
          </div>
        </SectionCard>

        <SectionCard className="lg:col-span-4" title="Monthly Audit Progress" subtitle={`Planned vs Completed — FY ${fy}`}>
          <div data-testid={TID.dashboard.lineMonthly}>
            <Chart
              type="area" height={280}
              series={[
                { name: "Planned", data: data.monthly_progress.map((m) => m.planned) },
                { name: "Completed", data: data.monthly_progress.map((m) => m.completed) },
              ]}
              options={{
                chart: { fontFamily: "IBM Plex Sans", toolbar: { show: false }, zoom: { enabled: false } },
                colors: ["#1D4ED8", "#10B981"],
                stroke: { curve: "smooth", width: 2 },
                fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0, stops: [0, 100] } },
                dataLabels: { enabled: false },
                grid: { show: true, borderColor: "#E2E8F0", strokeDashArray: 3 },
                xaxis: { categories: data.monthly_progress.map((m) => {
                  const [y, mm] = m.month.split("-");
                  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                  return `${names[parseInt(mm)-1]} '${y.slice(2)}`;
                }), labels: { style: { fontSize: "11px", colors: "#64748B" } } },
                yaxis: { labels: { style: { fontSize: "11px", colors: "#64748B" } } },
                legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
              }}
            />
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
        <SectionCard className="lg:col-span-4" title="Audit Score by Category" subtitle="Average weighted percentage per audit category">
          <div data-testid={TID.dashboard.barCategory}>
            <Chart
              type="bar" height={280}
              series={[{ name: "Avg Score", data: data.category_scores.map((c) => c.score) }]}
              options={{
                chart: { fontFamily: "IBM Plex Sans", toolbar: { show: false } },
                plotOptions: { bar: { horizontal: true, borderRadius: 4, dataLabels: { position: "top" }, barHeight: "60%" } },
                colors: ["#1D4ED8"],
                dataLabels: { enabled: true, formatter: (v) => `${v}%`, style: { colors: ["#0F172A"], fontSize: "11px", fontWeight: 700 }, offsetX: 25 },
                xaxis: { categories: data.category_scores.map((c) => c.category), max: 100, labels: { style: { fontSize: "11px", colors: "#64748B" } } },
                yaxis: { labels: { style: { fontSize: "12px", colors: "#0F172A", fontWeight: 500 } } },
                grid: { borderColor: "#E2E8F0", strokeDashArray: 3 },
              }}
            />
          </div>
        </SectionCard>

        <SectionCard className="lg:col-span-2" title="Grade Distribution" subtitle="Overall audit grades this FY">
          <div data-testid={TID.dashboard.donutGrade}>
            <Chart
              type="donut" height={280}
              series={[data.grade_distribution.A, data.grade_distribution.B, data.grade_distribution.C, data.grade_distribution.D]}
              options={{
                labels: ["Grade A", "Grade B", "Grade C", "Grade D"],
                colors: GRADE_COLORS,
                chart: { fontFamily: "IBM Plex Sans", toolbar: { show: false } },
                stroke: { width: 2, colors: ["#fff"] },
                legend: { position: "bottom", fontSize: "12px" },
                dataLabels: { enabled: true, style: { fontSize: "11px", fontWeight: 700 } },
                plotOptions: { pie: { donut: { size: "62%" } } },
              }}
            />
          </div>
        </SectionCard>
      </div>

      {/* Auditor-specific: My Assignments */}
      {isAuditor && (
        <SectionCard
          title="My Assignments"
          subtitle={`${myAssignmentsPage.rows.length} audit(s) assigned to you`}
          actions={
            myAssignmentsPage.rows.length > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Show</span>
                <Select value={String(myPageSize)} onValueChange={(v) => setMyPageSize(Number(v))}>
                  <SelectTrigger data-testid="my-assignments-page-size" className="w-16 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MY_ASSIGN_PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-slate-500">per page</span>
              </div>
            )
          }
        >
          <div data-testid={TID.dashboard.myAssignments}>
            {myAssignmentsPage.rows.length === 0 ? <EmptyState title="No assignments yet" /> : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
                      <th className="text-left px-3 py-2">Audit #</th>
                      <th className="text-left px-3 py-2">Supplier</th>
                      <th className="text-left px-3 py-2">Planned Date</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-right px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myAssignmentsPage.pageRows.map((a) => (
                      <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-3 py-2 font-mono text-xs">{a.audit_number}</td>
                        <td className="px-3 py-2 font-medium text-slate-900">{a.supplier?.name || "—"}</td>
                        <td className="px-3 py-2 text-slate-600 font-mono text-xs">{a.planned_date}</td>
                        <td className="px-3 py-2"><StatusBadge status={a.status} /></td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" data-testid={TID.audit.startBtn(a.id)} onClick={() => nav(`/audits/${a.id}`)} className="h-8 bg-sams-primary hover:bg-sams-primaryHover text-white">
                            <PlayCircle size={12} className="mr-1" /> Continue
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination footer */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                  <div className="text-xs text-slate-500">
                    Showing <span className="font-semibold text-slate-900">{myAssignmentsPage.pageStart + 1}</span>–
                    <span className="font-semibold text-slate-900">{Math.min(myAssignmentsPage.pageStart + myPageSize, myAssignmentsPage.rows.length)}</span>
                    {" of "}<span className="font-semibold text-slate-900">{myAssignmentsPage.rows.length}</span>
                    {" · Page "}<span className="font-semibold text-slate-900">{myAssignmentsPage.page}</span>{" of "}
                    <span className="font-semibold text-slate-900">{myAssignmentsPage.totalPages}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => setMyPage(1)} disabled={myAssignmentsPage.page === 1}
                      data-testid="my-assignments-first" className="h-8 px-2 text-xs">« First</Button>
                    <Button size="sm" variant="outline" onClick={() => setMyPage((p) => Math.max(1, p - 1))} disabled={myAssignmentsPage.page === 1}
                      data-testid="my-assignments-prev" className="h-8 px-2">
                      <ChevronLeft size={13} />
                    </Button>
                    {(() => {
                      const pages = [];
                      const start = Math.max(1, myAssignmentsPage.page - 2);
                      const end = Math.min(myAssignmentsPage.totalPages, start + 4);
                      for (let i = start; i <= end; i++) pages.push(i);
                      return pages.map((n) => (
                        <button key={n} onClick={() => setMyPage(n)} data-testid={`my-assignments-page-${n}`}
                          className={`h-8 min-w-[32px] px-2 text-xs rounded-md border transition-colors ${
                            n === myAssignmentsPage.page
                              ? "bg-sams-primary border-sams-primary text-white font-semibold"
                              : "border-slate-200 text-slate-700 hover:bg-slate-50"
                          }`}>{n}</button>
                      ));
                    })()}
                    <Button size="sm" variant="outline" onClick={() => setMyPage((p) => Math.min(myAssignmentsPage.totalPages, p + 1))} disabled={myAssignmentsPage.page === myAssignmentsPage.totalPages}
                      data-testid="my-assignments-next" className="h-8 px-2">
                      <ChevronRight size={13} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setMyPage(myAssignmentsPage.totalPages)} disabled={myAssignmentsPage.page === myAssignmentsPage.totalPages}
                      data-testid="my-assignments-last" className="h-8 px-2 text-xs">Last »</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </SectionCard>
      )}

      {/* SD Admin planning summary */}
      {isSDAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Total Plans" value={data.planning_summary.planned} icon={ClipboardList} tone="primary" />
          <StatCard label="Assigned" value={data.planning_summary.assigned} icon={CheckCircle2} tone="completed" />
          <StatCard label="Unassigned" value={data.planning_summary.unassigned} icon={Users} tone="in_progress" />
        </div>
      )}

      {/* Tables row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Top 5 Suppliers by Score" subtitle="Best performing suppliers across all audits">
          <div data-testid={TID.dashboard.topSuppliers}>
            {data.top_suppliers.length === 0 ? <EmptyState /> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
                    <th className="text-left px-3 py-2">Rank</th>
                    <th className="text-left px-3 py-2">Supplier</th>
                    <th className="text-right px-3 py-2">Audits</th>
                    <th className="text-right px-3 py-2">Avg Score</th>
                    <th className="text-right px-3 py-2">Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_suppliers.map((t, i) => {
                    const g = t.avg_score >= 90 ? "A" : t.avg_score >= 75 ? "B" : t.avg_score >= 60 ? "C" : "D";
                    return (
                      <tr key={t.supplier?.id || i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-3 py-2 font-mono text-xs text-slate-500">#{i + 1}</td>
                        <td className="px-3 py-2 font-medium text-slate-900">{t.supplier?.name || "—"}</td>
                        <td className="px-3 py-2 text-right text-slate-600">{t.audits}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">{t.avg_score}%</td>
                        <td className="px-3 py-2 text-right"><GradeBadge grade={g} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Upcoming Audits" subtitle="Scheduled within the next 7 days">
          <div data-testid={TID.dashboard.upcoming}>
            {data.upcoming.length === 0 ? <EmptyState /> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Supplier</th>
                    <th className="text-left px-3 py-2">Audit #</th>
                    <th className="text-right px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.upcoming.map((a) => (
                    <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => nav(`/audits/${a.id}`)}>
                      <td className="px-3 py-2 text-slate-700 font-mono text-xs">{a.planned_date}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{a.supplier?.name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{a.audit_number}</td>
                      <td className="px-3 py-2 text-right"><StatusBadge status={a.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
